import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { TensionLightingSection } from "@/components/TensionLightingSection";
import { PergolaUpgradeCta } from "@/components/PergolaUpgradeCta";
import { SailShadeBeforeAfter } from "@/components/SailShadeBeforeAfter";
import hero from "@/assets/original/tension.jpg";
import heroPicture from "@/assets/original/tension.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import beforeRed from "@/assets/sail-shade-before-red.jpg";
import afterBlack from "@/assets/sail-shade-after-black.jpg";
import phifertexFabric from "@/assets/sail-shade-phifertex-fabric.jpg";
import blueSewing from "@/assets/sail-shade-blue-sewing.jpg";
import blueUnderside from "@/assets/sail-shade-blue-underside.jpg";
import ballfieldCanopies from "@/assets/sail-shade-ballfield-blue-canopies.jpg";
import sailShadesSanAntonio from "@/assets/sail-shades-in-san-antonio.jpg.asset.json";
import playgroundSaSail from "@/assets/sail-shade-playground-tension-structure-san-antonio.jpg.asset.json";
import hyparNbSail from "@/assets/hypar-shade-sail-playground-tension-structure-new-braunfels.jpg.asset.json";
import triangleSeguinSail from "@/assets/triangle-shade-sail-park-playground-seguin-texas.jpg.asset.json";
import patioAustinSail from "@/assets/tensioned-shade-sail-patio-canopy-austin-texas.jpg.asset.json";
import coastalCcSail from "@/assets/coastal-blue-shade-sail-canopy-corpus-christi-texas.jpg.asset.json";
import ballfieldSaCanopy from "@/assets/ballfield-shade-canopy-tension-structure-san-antonio.jpg.asset.json";
import baseballSeguinCanopies from "@/assets/baseball-complex-spectator-shade-canopies-seguin-texas.jpg.asset.json";
import { assetUrl } from "@/lib/asset-url";

export const Route = createFileRoute("/sail-shades")({
  head: (ctx) => ({
      links: [canonicalLink(pathFromMatch(ctx))],
      meta: routeMeta(ctx, {
        title: "Sail Shades San Antonio & South Texas | Texas Made Shade",
        description: "Phifertex Plus sail shade re-cover in San Antonio & South Texas. Replace faded shade sail fabric on your existing frame — see before & after photos and.",
        image: hero,
        socialTitle: "Phifertex Plus Sail Shade Re-Cover — Before & After",
        socialDescription: "New Phifertex Plus mesh on the existing steel frame — South Texas sail shade re-cover by Texas Made Shade.",
        keywords: "Sail Shades San Antonio, sail shades San Antonio TX, shade sails San Antonio, sail shade company San Antonio, Sail Shades Austin, shade sails Austin, sail shades Austin TX, Awning Company San Antonio, awning company San Antonio TX, Awnings Austin TX, Phifertex Plus, Phifertex Plus re-cover, sail shade re-cover, shade sail fabric replacement, replace sail shade fabric, tension shade sails Texas, custom sail shades, shade sail before and after, awnings in San Antonio",
      }),
      scripts: [
        ...serviceHeadScripts({
          path: "/sail-shades",
          serviceName: "Sail Shades",
          breadcrumbName: "Sail Shades",
          description: "Custom Phifertex® mesh and canvas sail shades for patios, pools, playgrounds and commercial spaces across Texas.",
        }),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Service",
                "@id": "https://texasmadeshade.com/sail-shades#service",
                name: "Phifertex Plus Sail Shade Re-Cover & Custom Tension Shade Sails",
                serviceType: "Sail shade fabric replacement and custom tension shade sail fabrication",
                areaServed: ["San Antonio, TX", "New Braunfels, TX", "Seguin, TX", "Corpus Christi, TX", "Houston, TX", "South Texas"],
                provider: {
                  "@type": "LocalBusiness",
                  name: "Texas Made Shade & Covers",
                  telephone: "+1-830-390-9588",
                  url: "https://texasmadeshade.com",
                },
                description: "Custom sail shades and Phifertex Plus shade sail re-covers — engineered tension shade sails fabricated and installed across San Antonio and South Texas.",
              },
              {
                "@type": "ImageGallery",
                "@id": "https://texasmadeshade.com/sail-shades#before-after",
                name: "Phifertex Plus Sail Shade Re-Cover — Before & After",
                description: "Before and after photos of a Phifertex Plus sail shade fabric replacement on an existing steel frame in South Texas.",
                about: "Phifertex Plus shade sail re-cover",
              },
            ],
          }),
        },
      ],
    }),
  component: () => (
    <ServicePage
      currentPath="/sail-shades"
      serviceName="Sail Shades"
      keywordOutline={[
        { id: "overview", label: "Custom Sail Shades & Tension Structures in Texas" },
        { id: "why-us", label: "Why Texas Made Shade for Sail Shades" },
        { id: "details", label: "Sail Shade Fabric, Cables & Engineering" },
        { id: "applications", label: "Where We Install Sail Shades" },
        { id: "faqs", label: "Sail Shade FAQs" },
      ]}
      eyebrow="★ SAIL SHADES SAN ANTONIO ★"
      title="Custom Sail Shades in San Antonio, TX & Across South Texas"
      intro="Engineered tension shade sails for San Antonio backyards, schools, playgrounds, pools, and commercial properties — designed, fabricated, and installed by Texas Made Shade across San Antonio, the Hill Country, and the Texas Gulf Coast. Also available from the Awning Company San Antonio trusts for custom shade and outdoor curtains."
      heroImage={heroPicture}
      preContent={(
        <section className="bg-cream py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-2 lg:gap-14 lg:px-8">
            <div className="flex flex-col justify-center">
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
                ★ Ballfield & Spectator Shade
              </div>
              <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-navy-deep md:text-4xl">
                Engineered Sail Shades for Ballfields, Dugouts & Spectator Areas
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-navy-deep/85">
                From little-league dugouts to tournament complexes, our sail shades cover players, families, and concessions with the same welded steel posts and tensioned shade fabric we use on schools and public pools across Texas. Color-matched to team colors — installed without disturbing the playing surface.
              </p>
              <ul className="mt-6 grid gap-3 text-base text-navy-deep/85 sm:grid-cols-2">
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Welded steel posts in concrete footings
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Up to 95% UV-blocking shade fabric
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Color-matched to team or district colors
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Multi-panel layouts for full spectator coverage
                </li>
              </ul>
              <p className="mt-6 text-base text-navy-deep/75">
                Pictured: a multi-bay royal-blue sail shade canopy installed behind home plate — covering concessions, picnic tables, and family seating between the dugouts and the snack bar.
              </p>
            </div>
            <figure className="relative overflow-hidden border-4 border-navy-deep shadow-card">
              <img
                src={ballfieldCanopies}
                alt="Multi-bay royal-blue sail shade canopies installed by Texas Made Shade behind home plate at a Texas ballfield, covering the concession stand, picnic seating, and spectator area between the dugouts"
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <figcaption className="absolute bottom-0 left-0 right-0 bg-navy-deep/85 px-4 py-3 font-display text-xs uppercase tracking-wider text-white">
                Royal-blue sail shade canopies · Texas ballfield install
              </figcaption>
            </figure>
          </div>
        </section>
      )}
      features={[
        "Engineered for code compliance on schools & public spaces",
        "Heavy-duty steel posts with concrete footings",
        "UV-blocking shade fabric (up to 95% block)",
        "Custom geometry — triangle, hypar, multi-panel",
        "Removable for hurricane season on the coast",
        "Color matched to school or brand colors",
      ]}
      body={[
        { heading: "Schools & Playgrounds", text: "Large-span shade for play areas, drop-off lanes, and outdoor learning zones. Built to district spec." },
        { heading: "Pools & Splash Pads", text: "Cool the deck and protect swimmers. Marine-grade hardware that won't rust in chlorinated environments." },
        { heading: "Commercial Properties", text: "Outdoor seating, parking lots, and waiting areas. Branded shade that doubles as signage." },
        { heading: "Event & Venue Shade", text: "Wedding lawns, resort event venues, festival grounds and outdoor markets — tension sails that shade guests and photograph beautifully. Part of our Rio Grande Series™ hospitality work." },
      ]}
      faqs={[
        {
          q: "How long does a Phifertex® Plus sail shade last in San Antonio sun?",
          a: "Phifertex® Plus mesh holds up 8–12 years in direct South Texas and San Antonio sun before fading or losing UV protection. The welded aluminum or steel frame underneath lasts 20+ years — when the fabric needs replacing, we re-cover the existing posts.",
        },
        {
          q: "Can you re-cover my existing sail shade frame in San Antonio or the Hill Country?",
          a: "Yes — sail shade re-covers are one of our most common San Antonio and Hill Country jobs. We pattern new Phifertex® or canvas to your existing posts and tension hardware. Re-covers typically run 40–60% less than a full new sail shade install.",
        },
        {
          q: "How big can a single custom sail shade be over a Texas patio or playground?",
          a: "We've fabricated single sails over 40 ft on a side for Texas schools, playgrounds and commercial parking. Larger shaded areas use multiple overlapping sail shades on a shared post system — engineered for Texas wind loads.",
        },
        {
          q: "Do sail shades hold up to Texas wind, Hill Country storms and Coastal Bend hurricanes?",
          a: "Our sail shades use marine-grade stainless hardware, welded aluminum or galvanized steel posts, and engineered tension points. For Coastal Bend and hurricane-zone installs we add quick-release hardware so the fabric can be dropped before a major storm.",
        },
      ]}
      applications={["Schools", "Playgrounds", "Public Pools", "Parks", "Restaurants", "Event Venues", "Parking Lots"]}
      afterWhyUs={(
        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
              ★ Playground &amp; School Shade Sails
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-navy-deep md:text-4xl">
              Playground Shade Sails &amp; Tension Structures — Austin, TX
            </h2>
            <figure className="mt-6 mb-4 w-full border-4 border-navy-deep shadow-card md:float-right md:ml-8 md:w-[46%]">
              <img
                src={assetUrl(playgroundSaSail)}
                alt="Teal tensioned shade sail over a neighborhood playground in Austin, Texas — custom sail shade tension structure by Texas Made Shade"
                loading="lazy"
                className="w-full object-cover"
              />
              <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-wider text-white">
                Playground sail shade · Austin, TX
              </figcaption>
            </figure>
            <div className="text-lg leading-relaxed text-navy-deep/85 [&>p]:mt-4">
              <p>
                Playground equipment in an Austin summer can hit 140°F. A tensioned shade sail over the
                play deck drops surface temperature dramatically and blocks up to 95% of UV, which is the
                difference between a playground families use in July and one they walk past. We design,
                fabricate, and install these tension structures for ISDs, city parks, daycares, churches,
                and HOA neighborhood parks across Austin and the Hill Country.
              </p>
              <p>
                Every sail is patterned to the equipment footprint — hypar (twisted), triangle, or
                multi-panel geometry — on welded steel posts set in engineered concrete footings and rated
                to the local wind load. Fabric is UV-stable shade mesh in stock school and district colors,
                with stainless cable, turnbuckles, and D-rings at every corner.
              </p>
              <p>
                Photos here are our own Austin installs: a four-post teal sail over a fenced neighborhood
                playground, a center-post hypar sail that sheds Hill Country storm water fast, and a triangle
                sail over a park play area and picnic pavilion.
              </p>
            </div>
            <div className="clear-both grid gap-6 pt-10 md:grid-cols-2">
              <figure className="border-4 border-navy-deep shadow-card">
                <img
                  src={assetUrl(hyparNbSail)}
                  alt="Center-post hypar tension shade sail over playground equipment in Austin, Texas — twisted-geometry shade sail structure"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
                <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-wider text-white">
                  Hypar shade sail · Austin, TX
                </figcaption>
              </figure>
              <figure className="border-4 border-navy-deep shadow-card">
                <img
                  src={assetUrl(triangleSeguinSail)}
                  alt="Triangle tension shade sail over a park playground and picnic area in Austin, Texas — custom shade sail fabricated locally"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
                <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-wider text-white">
                  Triangle park sail shade · Austin, TX
                </figcaption>
              </figure>
            </div>
          </div>
        </section>
      )}
      letsTalkImageSrc={sailShadesSanAntonio.url}
      letsTalkImageAlt="Blue tension sail shade installed over a playground in San Antonio, Texas — custom shade sail by Texas Made Shade"
      cityCallouts={[
        {
          to: "/houston",
          city: "Houston",
          anchorText: "Engineered shade sails in Houston",
          blurb: "Tension shade sails for Houston schools, pool decks and commercial properties — removable for hurricane season on the Gulf Coast.",
        },
        {
          to: "/corpus-christi",
          city: "Corpus Christi",
          anchorText: "Coastal shade sails in Corpus Christi",
          blurb: "Marine-grade shade sails engineered for Coastal Bend wind loads, salt air and hurricane-conscious removal.",
        },
        {
          to: "/san-antonio",
          city: "San Antonio",
          anchorText: "Shade sails for San Antonio schools & parks",
          blurb: "Code-compliant tension structures for San Antonio playgrounds, pools and commercial spaces.",
        },
        {
          to: "/seguin",
          city: "Seguin",
          anchorText: "Sail shades fabricated in Seguin, TX",
          blurb: "Tension sail shade panels patterned and stitched in our Seguin shop, then installed on welded steel posts across South Texas.",
        },
      ]}
      gallerySection={{
        eyebrow: "Phifertex Plus Sail Shade Re-Cover — Before & After",
        title: "Phifertex Plus Sail Shade Re-Cover: Before & After in South Texas",
        intro: (
          <>
            <SailShadeBeforeAfter beforeSrc={beforeRed} afterSrc={afterBlack} />

            <aside
              aria-labelledby="sail-shade-project-summary"
              className="mt-10 border-2 border-navy-deep/15 bg-cream shadow-card"
            >
              <div className="border-b-2 border-navy-deep/10 bg-white px-6 py-5">
                <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
                  ★ Project Summary
                </div>
                <h3
                  id="sail-shade-project-summary"
                  className="mt-2 font-display text-2xl font-bold leading-tight text-navy-deep md:text-3xl"
                >
                  Phifertex Plus Re-Cover · South Texas Backyard
                </h3>
                <p className="mt-3 text-base text-navy-deep/80">
                  A faded, sun-damaged residential sail shade brought back to life with brand-new Phifertex Plus mesh on the original steel posts. No demo, no new footings — just a clean fabric swap and upgraded marine-grade hardware, finished in about a week.
                </p>
              </div>
              <dl className="grid gap-px bg-navy-deep/10 sm:grid-cols-2 lg:grid-cols-4">
                <div className="bg-cream p-5">
                  <dt className="font-display text-[11px] uppercase tracking-[0.25em] text-red">Fabric Type</dt>
                  <dd className="mt-2 font-display text-lg font-bold text-navy-deep">
                    Phifertex Plus
                    <span className="mt-1 block font-sans text-sm font-normal text-navy-deep/70">Vinyl-coated polyester mesh, ~90% UV block.</span>
                  </dd>
                </div>
                <div className="bg-cream p-5">
                  <dt className="font-display text-[11px] uppercase tracking-[0.25em] text-red">Frame</dt>
                  <dd className="mt-2 font-display text-lg font-bold text-navy-deep">
                    Existing Frame Reused
                    <span className="mt-1 block font-sans text-sm font-normal text-navy-deep/70">Original steel posts &amp; footings stayed put.</span>
                  </dd>
                </div>
                <div className="bg-cream p-5">
                  <dt className="font-display text-[11px] uppercase tracking-[0.25em] text-red">Location</dt>
                  <dd className="mt-2 font-display text-lg font-bold text-navy-deep">
                    South Texas
                    <span className="mt-1 block font-sans text-sm font-normal text-navy-deep/70">Residential backyard · San Antonio area.</span>
                  </dd>
                </div>
                <div className="bg-cream p-5">
                  <dt className="font-display text-[11px] uppercase tracking-[0.25em] text-red">Timeline</dt>
                  <dd className="mt-2 font-display text-lg font-bold text-navy-deep">
                    ~1 Week
                    <span className="mt-1 block font-sans text-sm font-normal text-navy-deep/70">Measure → fabricate → install (½-day on site).</span>
                  </dd>
                </div>
              </dl>
            </aside>


            <div className="mt-10 border-2 border-navy-deep bg-navy-deep text-white shadow-card">
              <div className="grid gap-0 md:grid-cols-[1.4fr_1fr]">
                <div className="border-b-2 border-white/15 p-6 md:border-b-0 md:border-r-2 md:p-8">
                  <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
                    ★ Ready For Your Re-Cover?
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-bold leading-tight md:text-3xl">
                    Get a free estimate on your Phifertex Plus sail shade re-cover.
                  </h3>
                  <p className="mt-3 text-base text-white/85">
                    Send a few photos and rough measurements — we'll respond same business day with a free, no-pressure estimate. Backyard, school, pool, or commercial.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <a
                      href={`/#lets-talk?type=${encodeURIComponent("Sail Shade / Tension Structure")}&notes=${encodeURIComponent("Sail shade re-cover inquiry — please describe location, approximate sail size, and existing fabric condition.")}`}
                      className="inline-flex items-center gap-2 bg-red px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-red/90"
                    >
                      Get a Free Estimate
                    </a>
                    <a
                      href="tel:+18303909588"
                      className="inline-flex items-center gap-2 border-2 border-white px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
                    >
                      Call +1 (830) 390-9588
                    </a>
                    <a
                      href="/contact"
                      className="inline-flex items-center gap-2 border-2 border-white/40 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white/90 transition hover:border-white hover:text-white"
                    >
                      Contact Us
                    </a>
                  </div>
                </div>
                <figure className="flex flex-col justify-center bg-cream p-6 text-navy-deep md:p-8">
                  <div className="font-display text-xs uppercase tracking-[0.3em] text-red" aria-hidden="true">
                    ★★★★★
                  </div>
                  <blockquote className="mt-3 font-display text-lg italic leading-snug text-navy-deep">
                    “Texas Made Shade re-covered our backyard sail with new Phifertex Plus and it looks better than the day we installed the original. Crew was on time, install was clean, and the patio is noticeably cooler.”
                  </blockquote>
                  <figcaption className="mt-4 text-sm font-semibold uppercase tracking-wider text-navy-deep/75">
                    — Jennifer M., Homeowner · San Antonio, TX
                  </figcaption>
                </figure>
              </div>
            </div>
          </>
        ),
        subsections: [
          {
            heading: "Fabric Used",
            body: (
              <>
                <strong>Phifertex Plus</strong> vinyl-coated polyester mesh — UV-stable, mildew-resistant, and rated for ~90% UV block. Sewn with PTFE marine-grade thread that won't rot in Texas sun.
              </>
            ),
          },
          {
            heading: "Project Timeframe",
            body: (
              <>
                <strong>About 1 week</strong> from on-site measurement to re-install. Pattern + fabricate in our Seguin shop (3–4 days), then a half-day install with the existing posts. It all depends on current projects, which is changing daily.
              </>
            ),
          },
          {
            heading: "What Was Replaced",
            body: (
              <>
                <strong>Shade fabric and tensioning hardware only</strong> — the existing steel posts, footings, and frame stayed in place. New 316 stainless turnbuckles, D-rings, and edge cable upgraded for long-term salt + sun exposure.
              </>
            ),
          },
        ],
        columns: 3,
        images: [
          {
            src: phifertexFabric,
            alt: "Roll of Phifertex Plus shade fabric on the Texas Made Shade cutting table in Seguin",
            label: "Phifertex Plus · In The Shop",
            caption: "The actual fabric we use — Phifertex Plus mesh, hand-cut to your sail's pattern in our Seguin shop.",
          },
          {
            src: blueSewing,
            alt: "Sewing a large blue Phifertex Plus shade sail panel on the industrial line in the Seguin shop",
            label: "Industrial Sewing · Sail Panel",
            caption: "Heavy-duty industrial stitching with PTFE marine-grade thread — the seams that hold your shade tight for years.",
          },
          {
            src: blueUnderside,
            alt: "Underside view of a finished blue Phifertex Plus sail shade installed over a Texas patio, showing taut tensioning and corner hardware",
            label: "Installed Underside · Tensioned Sail",
            caption: "Looking up from under a finished install — taut tension, clean edges, and stainless corner hardware.",
          },
        ],
        cta: {
          headline: "Get a Free Estimate on Your Custom Sail Shade",
          subhead: "Backyard, school, pool, or commercial property — send a few photos and rough measurements and we'll come back same business day with sizing questions and a free, no-pressure estimate. Engineered, fabricated, and installed by Texas Made Shade.",
          buttonLabel: "Get a Free Estimate",
          href: `/#lets-talk?type=${encodeURIComponent("Sail Shade / Tension Structure")}&notes=${encodeURIComponent("Sail shade inquiry — please describe the location (backyard, school, pool, commercial), approximate area to cover, mounting points (existing posts / new posts), and timeline.")}`,
          secondaryButtonLabel: "Request a Quote",
          secondaryHref: "/contact",
          phoneNumber: "+1 (830) 390-9588",
        },
      }}
      testimonials={{
        eyebrow: "Customer Reviews",
        title: "What Texas Customers Say About Our Sail Shades",
        items: [
          {
            quote: "Texas Made Shade re-covered our backyard sail with new Phifertex Plus and it looks better than the day we installed the original. Crew was on time, install was clean, and the shade cools the patio noticeably.",
            author: "Jennifer M.",
            role: "Homeowner",
            location: "San Antonio, TX",
            rating: 5,
          },
          {
            quote: "We needed engineered sail shades over our school playground. They handled the drawings, permitting prep, fabrication, and install — all in-house. Kids and parents love the new shade.",
            author: "David T.",
            role: "Facilities Director",
            location: "New Braunfels, TX",
            rating: 5,
          },
          {
            quote: "Removable sail shades over our pool deck for hurricane season. Hardware is marine grade, posts are solid, and the install crew clearly knew what they were doing. Highly recommend.",
            author: "Rachel S.",
            role: "Property Owner",
            location: "Corpus Christi, TX",
            rating: 5,
          },
        ],
      }}
      projectHighlights={{
        eyebrow: "Project Details",
        title: "Sail Shades & Tension Structures — At A Glance",
        intro: "Quick specs from this featured re-cover project. Replace with the actual job details when ready.",
        items: [
          {
            title: "Location",
            description: "Residential / commercial property in the Texas Hill Country (placeholder — update with actual city, neighborhood, or property type).",
            specs: [
              "City: [Add city, TX]",
              "Property type: [Residential / Commercial]",
              "Service area: South Texas / Hill Country",
              "Install crew: Texas Made Shade in-house team",
            ],
          },
          {
            title: "Size & Geometry",
            description: "Custom-patterned tension sail dimensions (placeholder — swap in measured square footage and panel count).",
            specs: [
              "Approx. coverage: [XX'] × [XX'] (~[XXX] sq. ft.)",
              "Panel count: [#] sail panels",
              "Geometry: [Triangle / Hypar / Multi-panel]",
              "Mounting points: [#] anchor points",
              "Existing steel frame re-used",
            ],
          },
          {
            title: "Materials & Hardware",
            description: "Premium UV-stable shade fabric paired with marine-grade tensioning hardware. Pergola covers and welded aluminum frames built in Seguin, TX.",
            specs: [
              "Fabric: Phifertex Plus vinyl-coated mesh",
              "UV block: ~90% (color dependent)",
              "Thread: PTFE marine-grade UV thread",
              "Hardware: 316 stainless turnbuckles & D-rings",
              "Frame: Existing schedule-40 steel posts",
            ],
          },
        ],
      }}
      postCta={<PergolaUpgradeCta />}
    >
      <section className="bg-cream py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
            ★ Patio, Pool &amp; Coastal Sail Shades
          </div>
          <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-navy-deep md:text-4xl">
            Backyard &amp; Coastal Shade Sails — Austin, San Antonio &amp; Corpus Christi
          </h2>
          <figure className="mt-6 mb-4 w-full border-4 border-navy-deep shadow-card md:float-left md:mr-8 md:w-[46%]">
            <img
              src={assetUrl(patioAustinSail)}
              alt="Overhead view of a charcoal tensioned shade sail canopy over a patio dining area in Austin, Texas — custom tension shade structure"
              loading="lazy"
              className="w-full object-cover"
            />
            <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-wider text-white">
              Tensioned patio shade sail · Austin, TX
            </figcaption>
          </figure>
          <div className="text-lg leading-relaxed text-navy-deep/85 [&>p]:mt-4">
            <p>
              Residential sail shades cover patios, pool decks, hot tubs, and outdoor kitchens without the
              cost or permitting of a solid roof. Rectangular multi-point sails like this Austin patio
              canopy stretch across dining and lounge zones on slim wall brackets and posts, so the shade
              lands where people sit while the structure stays out of the sightline.
            </p>
            <p>
              On the coast — Corpus Christi, Port Aransas, Rockport, and the Coastal Bend — we build the
              same sails with 316 stainless hardware and quick-release corners so the fabric can be dropped
              ahead of a named storm and re-tensioned in an afternoon. Salt air is the reason the hardware
              spec matters more than the fabric color.
            </p>
            <p>
              Whether it's a single triangle over a play area or a four-point sail across a pool deck, every
              panel is patterned and stitched in our Seguin shop with PTFE marine thread and installed by
              our own crews.
            </p>
          </div>
          <figure className="clear-both mt-10 border-4 border-navy-deep shadow-card">
            <img
              src={assetUrl(coastalCcSail)}
              alt="Royal blue triangle shade sail canopy at a coastal park near Corpus Christi, Texas — hurricane-conscious tension shade structure"
              loading="lazy"
              className="w-full object-cover"
            />
            <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-wider text-white">
              Coastal blue shade sail canopy · Corpus Christi, TX
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
            ★ Athletic &amp; Spectator Shade Structures
          </div>
          <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-navy-deep md:text-4xl">
            Ballfield &amp; Spectator Shade Canopies — Seguin, San Antonio &amp; South Texas
          </h2>
          <figure className="mt-6 mb-4 w-full border-4 border-navy-deep shadow-card md:float-right md:ml-8 md:w-[46%]">
            <img
              src={assetUrl(ballfieldSaCanopy)}
              alt="Royal blue hip-end shade canopy tension structure over a ballfield concourse in Seguin, Texas"
              loading="lazy"
              className="w-full object-cover"
            />
            <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-wider text-white">
              Ballfield shade canopy · Seguin, TX
            </figcaption>
          </figure>
          <div className="text-lg leading-relaxed text-navy-deep/85 [&>p]:mt-4">
            <p>
              Tournament complexes, little-league parks, and school athletic facilities need shade that
              spans concourses, concessions, bleachers, and batting areas without posts in the way of play.
              These hip-end tension canopies run in bays down the walkway on powder-coated steel columns,
              anchored behind the backstop netting and set in engineered footings.
            </p>
            <p>
              Fabric is high-UV shade mesh in team colors, tensioned over welded rafters so water sheds and
              wind passes through. Panels come off for the offseason or storm prep, and individual bays can
              be re-covered years later without touching the steel.
            </p>
            <p>
              We handle drawings, fabrication, and install in-house, with PO-ready quotes for districts,
              cities, and league boards across Seguin, San Antonio, New Braunfels, and the rest of South
              Texas.
            </p>
          </div>
          <figure className="clear-both mt-10 border-4 border-navy-deep shadow-card">
            <img
              src={assetUrl(baseballSeguinCanopies)}
              alt="Multi-bay royal blue spectator shade canopies over the concession concourse at a baseball complex in Seguin, Texas"
              loading="lazy"
              className="w-full object-cover"
            />
            <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-wider text-white">
              Multi-bay spectator shade canopies · Seguin, TX
            </figcaption>
          </figure>
        </div>
      </section>
    <TensionLightingSection />
    </ServicePage>
  ),
});
